import { useTranslation } from 'react-i18next'
import { useFormContext, useWatch } from 'react-hook-form'
import {
  GreetingSection,
  LanguageSelector,
  SortableLanguagesInput,
  LexiconEditDialog,
} from '../index'

/**
 * Languages & Greeting Tab - Language settings and greeting
 *
 * Includes:
 * - Primary language selector with flag emoji
 * - Additional languages with drag-to-reorder
 * - Pronunciation/Lexicon edit dialog
 * - Greeting message section (dynamic/personalized toggles)
 *
 * Voice selection lives on the Voices tab.
 */
export function PersonalityTab() {
  const { t } = useTranslation()
  const { control, setValue } = useFormContext()

  // Watch form values
  const assistantId = useWatch({ control, name: 'id' }) as number
  const defaultLanguage = useWatch({ control, name: 'default_language' }) as string
  const additionalLanguages = useWatch({ control, name: 'additional_languages' }) as string[]

  // Handle primary language change
  const handleLanguageChange = (value: string) => {
    setValue('default_language', value, { shouldDirty: true })
  }

  // Handle additional languages change
  const handleAdditionalLanguagesChange = (value: string[]) => {
    setValue('additional_languages', value, { shouldDirty: true })
  }

  return (
    <div className="space-y-10">
      {/* Language Settings Section */}
      <section>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
          {t('resources.assistants.personality.languages', { defaultValue: 'Languages' })}
        </h2>

        <div className="mt-6 rounded-xl border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-900">
          <div className="space-y-8">
            {/* Primary Language */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                {t('resources.assistants.fields.default_language')}
              </label>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                {t('resources.assistants.personality.primary_language_description', {
                  defaultValue:
                    'This is the language that will be spoken when a customer reaches your business.',
                })}
              </p>
              <div className="mt-3 max-w-md">
                <LanguageSelector
                  value={defaultLanguage}
                  onChange={handleLanguageChange}
                  placeholder={t('resources.assistants.personality.select_primary_language', {
                    defaultValue: 'Select primary language',
                  })}
                />
              </div>
            </div>

            {/* Additional Languages */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                {t('resources.assistants.fields.additional_languages')}
              </label>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                {t('resources.assistants.personality.additional_languages_description', {
                  defaultValue:
                    'Callers can select their preferred language by pressing a number on the keypad.',
                })}
              </p>
              <div className="mt-3">
                <SortableLanguagesInput
                  value={additionalLanguages || []}
                  onChange={handleAdditionalLanguagesChange}
                  excludeLanguages={defaultLanguage ? [defaultLanguage] : []}
                />
              </div>
            </div>

            {/* Pronunciation / Lexicon */}
            <LexiconEditDialog />
          </div>
        </div>
      </section>

      {/* Greeting Message Section */}
      <section>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
          {t('resources.assistants.personality.greeting_message')}
        </h2>

        <div className="mt-6 rounded-xl border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-900">
          <GreetingSection assistantId={assistantId} />
        </div>
      </section>
    </div>
  )
}

export default PersonalityTab
